import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { RENDER } from "@/constants/testIds";
import { RefreshCw, Download, Package, Ban, Trash2, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const statusPill = {
  queued:    "bg-gray-100 text-gray-600 ring-gray-200",
  rendering: "bg-amber-50 text-amber-700 ring-amber-200",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  failed:    "bg-red-50 text-red-700 ring-red-200",
};

export default function Renders() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const load = async () => setItems((await api.get("/renders")).data);
  useEffect(() => {
    load();
    const id = setInterval(load, 2500);
    return () => clearInterval(id);
  }, []);

  const retry = async (id) => { await api.post(`/renders/${id}/retry`); toast.success("Re-queued"); load(); };
  const cancel = async (id) => {
    try { await api.post(`/renders/${id}/cancel`); toast.success("Render cancelled"); load(); }
    catch { toast.error("Cancel failed"); }
  };
  const del = async (id) => {
    try {
      await api.delete(`/renders/${id}`);
      const s = new Set(selected); s.delete(id); setSelected(s);
      toast.success("Row removed"); load();
    } catch { toast.error("Remove failed"); }
  };
  const toggleSel = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const zip = async () => {
    if (!selected.size) { toast.error("Select renders to ZIP"); return; }
    try {
      const res = await api.post("/renders/zip", { render_job_ids: [...selected] }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "codira-renders.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
    } catch { toast.error("ZIP failed"); }
  };

  const clearStuck = async () => {
    if (!confirm("Mark every queued/rendering job as failed? Use this if jobs are wedged after a deploy.")) return;
    try {
      const res = await api.post("/renders/clear-stuck");
      toast.success(`Cleared ${res.data.cleared} stuck job(s)`);
      load();
    } catch { toast.error("Clear failed"); }
  };

  const stuckCount = items.filter(i => i.status === "queued" || i.status === "rendering").length;

  return (
    <div className="p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display text-gray-900">Renders</h1>
          <p className="text-sm text-gray-500 mt-1">Live status of every render job.</p>
        </div>
        <div className="flex items-center gap-2">
          {stuckCount > 0 && (
            <button data-testid="clear-stuck-btn" onClick={clearStuck}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition">
              <Ban className="h-3.5 w-3.5" /> Clear {stuckCount} stuck
            </button>
          )}
          <button data-testid={RENDER.zipBtn} onClick={zip} disabled={!selected.size}
            className="btn-brand inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
            <Package className="h-4 w-4" /> ZIP selected ({selected.size})
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider font-medium text-gray-500">
          <div className="col-span-1">Sel</div>
          <div className="col-span-3">Job ID</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3">Progress</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">No renders yet.</div>
        )}
        {items.map((j) => (
          <div key={j.id} data-testid={RENDER.card(j.id)}
            className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-b-0 items-center text-sm hover:bg-gray-50/60 transition-colors">
            <div className="col-span-1">
              <input type="checkbox" checked={selected.has(j.id)} onChange={() => toggleSel(j.id)}
                disabled={j.status !== "completed"}
                className="accent-brand w-3.5 h-3.5" />
            </div>
            <div className="col-span-3 text-xs text-gray-600 font-mono truncate">{j.id}</div>
            <div className="col-span-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md ring-1 text-[10px] font-medium uppercase tracking-wider ${statusPill[j.status]}`}>
                {j.status}
              </span>
              {j.error && <p className="text-[10px] text-red-600 mt-1 truncate" title={j.error}>{j.error}</p>}
            </div>
            <div className="col-span-3 pr-3">
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand transition-all" style={{ width: `${j.progress || 0}%` }} />
              </div>
              <p className="text-[10px] text-gray-500 mt-1 tabular-nums">{j.progress || 0}%</p>
            </div>
            <div className="col-span-2 text-xs text-gray-500">{new Date(j.created_at).toLocaleString()}</div>
            <div className="col-span-1 flex justify-end gap-1">
              {j.status === "completed" && j.output_url && (
                <>
                  <Link to={`/renders/${j.id}/publish/meta`} title="Publish to Meta"
                    className="p-1.5 rounded-md border border-gray-200 hover:border-brand hover:text-brand transition-colors">
                    <Rocket className="h-3.5 w-3.5" />
                  </Link>
                  <a href={fileUrl(`${j.output_url}?download=1`)} download title="Download MP4"
                    className="p-1.5 rounded-md border border-gray-200 hover:border-brand hover:text-brand transition-colors">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </>
              )}
              {(j.status === "failed" || j.status === "rendering" || j.status === "queued") && (
                <button data-testid={RENDER.retryBtn(j.id)} onClick={() => retry(j.id)}
                  title={j.status === "rendering" ? "Worker stuck? Force retry" : "Retry"}
                  className="p-1.5 rounded-md border border-gray-200 hover:border-brand hover:text-brand transition-colors">
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              )}
              {(j.status === "queued" || j.status === "rendering") && (
                <button onClick={() => cancel(j.id)} title="Cancel"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:border-amber-500 hover:text-amber-600 transition-colors">
                  <Ban className="h-3.5 w-3.5" />
                </button>
              )}
              {(j.status === "completed" || j.status === "failed") && (
                <button onClick={() => del(j.id)} title="Remove row"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-400 hover:border-red-500 hover:text-red-500 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
